import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { CheckCircle2, Clock } from "lucide-react";

// Employee ID shape: "RKF" + 2 to 4 digits. Leading zeros kept exactly as typed
// (RKF01 valid, RKF1 invalid). Validated client-side AND server-side.
const EMP_CODE_RE = /^RKF\d{2,4}$/;

export default function Register() {
  const [form, setForm] = useState({ name: "", email: "", employee_code: "", password: "", designation: "", department: "" });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [empError, setEmpError] = useState("");
  const [employees, setEmployees] = useState([]);
  const navigate = useNavigate();

  // Masters are published read-only so the register page can auto-fill
  // designation + department when the typed ID matches a known employee.
  useEffect(() => {
    api.get("/employees/public-roster")
      .then(r => setEmployees(r.data || []))
      .catch(() => setEmployees([]));
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Uppercase + trim as the user types. Validate on blur so we don't flash
  // errors mid-keystroke.
  const onEmpChange = (raw) => {
    const cleaned = raw.toUpperCase().replace(/\s+/g, "");
    set("employee_code", cleaned);
    if (!cleaned) { setEmpError(""); return; }
    if (!EMP_CODE_RE.test(cleaned)) {
      setEmpError("Employee ID must look like RKF01 or RKF247");
    } else {
      setEmpError("");
      // Auto-fill department + designation if the ID matches a master row.
      const emp = (employees || []).find((e) => (e.employee_code || "").toUpperCase() === cleaned);
      if (emp) {
        setForm((f) => ({
          ...f,
          department: f.department || emp.department || "",
          designation: f.designation || emp.designation || "",
        }));
      }
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    const code = form.employee_code.trim().toUpperCase();
    if (!EMP_CODE_RE.test(code)) {
      setEmpError("Employee ID must look like RKF01 or RKF247");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/register", { ...form, employee_code: code });
      setSubmitted(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Registration failed");
    } finally { setLoading(false); }
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <Card className="w-full max-w-lg" data-testid="register-pending-card">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-emerald-100 mx-auto flex items-center justify-center mb-4">
              <CheckCircle2 className="w-9 h-9 text-emerald-600" />
            </div>
            <h2 className="font-display text-2xl font-bold">Account created</h2>
            <div className="mt-3 inline-flex items-center gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-3 py-1">
              <Clock className="w-3.5 h-3.5" /> Awaiting Accounts approval
            </div>
            <p className="text-sm text-slate-600 mt-4 leading-relaxed">
              Your account has been created and is waiting for the <b>Accounts team</b> to authorise access.
              You'll receive an email at <b className="font-mono">{form.email}</b> the moment it's approved — usually within a few working hours.
            </p>
            <div className="mt-6 flex flex-col gap-2">
              <Button onClick={() => navigate("/login")} className="w-full bg-[#0F172A] hover:bg-slate-800" data-testid="register-go-to-login">
                Go to sign in
              </Button>
              <Link to="/" className="text-xs text-slate-500 hover:text-slate-700 mt-1">Back to home</Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <Card className="w-full max-w-lg">
        <CardContent className="p-8">
          <h2 className="font-display text-2xl font-bold">Create account</h2>
          <p className="text-sm text-slate-500 mt-1">
            New accounts require <b>Accounts team</b> approval before you can sign in.
          </p>
          <form onSubmit={submit} className="space-y-4 mt-6">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label>Full name</Label>
                <Input value={form.name} onChange={(e) => set("name", e.target.value)} required data-testid="register-name" />
              </div>
              <div className="col-span-2">
                <Label>Email</Label>
                <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} required data-testid="register-email" />
              </div>
              <div className="col-span-2">
                <Label>Employee ID *</Label>
                <Input
                  value={form.employee_code}
                  onChange={(e) => onEmpChange(e.target.value)}
                  onBlur={() => {
                    if (form.employee_code && !EMP_CODE_RE.test(form.employee_code))
                      setEmpError("Employee ID must look like RKF01 or RKF247");
                  }}
                  placeholder="RKF01 … RKF247"
                  required
                  className={empError ? "border-rose-400" : ""}
                  data-testid="register-emp-code"
                />
                {empError && (
                  <div className="text-xs text-rose-600 mt-1" data-testid="register-emp-code-error">{empError}</div>
                )}
              </div>
              <div className="col-span-2">
                <Label>Password</Label>
                <Input type="password" value={form.password} onChange={(e) => set("password", e.target.value)} required data-testid="register-password" />
              </div>
              <div>
                <Label>Department</Label>
                <Input value={form.department} onChange={(e) => set("department", e.target.value)} data-testid="register-department" />
              </div>
              <div>
                <Label>Designation</Label>
                <Input value={form.designation} onChange={(e) => set("designation", e.target.value)} data-testid="register-designation" />
              </div>
            </div>
            <Button type="submit" disabled={loading || !!empError} className="w-full bg-[#0F172A] hover:bg-slate-800" data-testid="register-submit-btn">
              {loading ? "Creating..." : "Request access"}
            </Button>
            <p className="text-xs text-slate-500 text-center">
              Have an account? <Link to="/login" className="text-blue-600 font-semibold">Sign in</Link>
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
